import React, { createContext, useContext, useState, useEffect } from "react";

const PaginationContext = createContext();
const DataContext = createContext();

const STORAGE_KEY = "table_item_per_page";
const DEFAULT_ITEMS_PER_PAGE = 10;

export const TableProvider = ({ children, tableId }) => {
  if (!tableId) {
    throw new Error("TableProvider requires a unique 'tableId' prop");
  }

  // Load saved preference for this table only
  const getSavedItemsPerPage = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const allPreference = JSON.parse(saved);
        const value = allPreference?.[tableId];
        if (typeof value === "number") {
          return value;
        }
      }
    } catch (e) {
      console.warn(`Failed to read itemsPerPage for table "${tableId}"`, e);
    }
    return DEFAULT_ITEMS_PER_PAGE;
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(getSavedItemsPerPage);
  const [totalDocs, setTotalDocs] = useState(10);

  const [sortBy, setSortBy] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refetch, setRefetch] = useState(0);

  // Save only this table's preference when itemsPerPage changes
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      const itemsPerPageConfig = saved ? JSON.parse(saved) : {};

      const newItemsPerPageConfig = {
        ...itemsPerPageConfig,
        [tableId]: itemsPerPage,
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(newItemsPerPageConfig));
    } catch (e) {
      console.warn(`Failed to save itemsPerPage for table "${tableId}"`, e);
    }
  }, [itemsPerPage, tableId]);

  return (
    <PaginationContext.Provider
      value={{
        currentPage,
        setCurrentPage,
        itemsPerPage,
        setItemsPerPage,
        totalDocs,
        setTotalDocs,
      }}
    >
      <DataContext.Provider
        value={{
          data,
          loading,
          refetch,
          setData,
          setLoading,
          setRefetch,
          sortBy,
          setSortBy,
          searchTerm,
          setSearchTerm,
        }}
      >
        {children}
      </DataContext.Provider>
    </PaginationContext.Provider>
  );
};

export const usePagination = () => {
  const ctx = useContext(PaginationContext);
  if (!ctx) throw new Error("usePagination must be used inside TableProvider");
  return ctx;
};

export const useTable = () => {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useTable must be used inside TableProvider");
  return ctx;
};